import { ReactNode } from "react";
import { Navigate, createBrowserRouter } from "react-router-dom";
import { useAuth } from "./lib/auth";
import { Cabinet } from "./pages/Cabinet";
import { Layout } from "./components/shared/Layout";
import { Login } from "./pages/Auth/Login";
import { Signup } from "./pages/Auth/Signup";
import { ForgotPassword } from "./pages/Auth/ForgotPassword";
import { ResetPassword } from "./pages/Auth/ResetPassword";
import { TelegramApp } from "./pages/TelegramApp";
import { Certificate } from "./pages/Certificate";
import { Main } from "./pages/Main";
import { Presentation } from "./pages/Presentation";
import { Profile } from "./pages/Profile";
import { SubmitProject } from "./pages/SubmitProject";
import { MyProjects } from "./pages/MyProjects";
import { Notifications } from "./pages/Notifications";
import { ProjectCard } from "./pages/ProjectCard";
import { AdminPartners } from "./pages/admin/Partners";
import { AdminPartnerCard } from "./pages/admin/PartnerCard";
import { AdminProjects } from "./pages/admin/Projects";
import { AdminProjectCard } from "./pages/admin/ProjectCard";
import { AdminMessages } from "./pages/admin/Messages";
import { Loader } from "./components/ui/Loader";

const RequireAuth = ({ children, roles }: { children: ReactNode; roles?: string[] }) => {
  const { user, role, loading } = useAuth();

  if (loading) {
    return <Loader />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (roles) {
    if (!role) return <Loader />;
    if (!roles.includes(role)) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

export const router = createBrowserRouter([
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/signup",
    element: <Signup />,
  },
  {
    path: "/forgot",
    element: <ForgotPassword />,
  },
  {
    path: "/reset",
    element: <ResetPassword />,
  },
  {
    path: "/tg-app",
    element: <TelegramApp />,
  },
  {
    path: "/",
    element: <Layout />,
    children: [
      { index: true, element: <Cabinet /> },
      {
        path: "dashboard",
        element: (
          <RequireAuth>
            <Main />
          </RequireAuth>
        ),
      },
      {
        path: "profile",
        element: (
          <RequireAuth>
            <Profile />
          </RequireAuth>
        ),
      },
      {
        path: "submit",
        element: (
          <RequireAuth>
            <SubmitProject />
          </RequireAuth>
        ),
      },
      {
        path: "projects",
        element: (
          <RequireAuth>
            <MyProjects />
          </RequireAuth>
        ),
      },
      {
        path: "projects/:id",
        element: (
          <RequireAuth>
            <ProjectCard />
          </RequireAuth>
        ),
      },
      {
        path: "notifications",
        element: (
          <RequireAuth>
            <Notifications />
          </RequireAuth>
        ),
      },
      {
        path: "admin/partners",
        element: (
          <RequireAuth roles={["admin", "manager"]}>
            <AdminPartners />
          </RequireAuth>
        ),
      },
      {
        path: "admin/partners/:id",
        element: (
          <RequireAuth roles={["admin", "manager"]}>
            <AdminPartnerCard />
          </RequireAuth>
        ),
      },
      {
        path: "admin/projects",
        element: (
          <RequireAuth roles={["admin", "manager"]}>
            <AdminProjects />
          </RequireAuth>
        ),
      },
      {
        path: "admin/projects/:id",
        element: (
          <RequireAuth roles={["admin", "manager"]}>
            <AdminProjectCard />
          </RequireAuth>
        ),
      },
      {
        path: "admin/messages",
        element: (
          <RequireAuth roles={["admin", "manager"]}>
            <AdminMessages />
          </RequireAuth>
        ),
      },
    ],
  },
  {
    path: "/:id",
    element: <Certificate />,
  },
  {
    path: "/presentation",
    element: <Presentation />,
  },
]);
